import { useRef } from 'react';
import { styled } from '@mui/material/styles';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const FloatingActionButtonContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(4),
  right: theme.spacing(4),
}));

const EditorWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'minHeight',
})<{ minHeight?: number }>(({ minHeight }) => ({
  position: 'relative',

  '& .ck-editor__editable': {
    minHeight: minHeight ? `${minHeight}px !important` : undefined,
    '& ol, & ul': {
      margin: '0.3rem 0 0.3rem 0',
      paddingLeft: '1.5rem',
      listStylePosition: 'outside',
    },
    '& ul > li': {
      listStyle: 'disc outside',
      position: 'relative',
      marginBottom: '0.5rem',
      lineHeight: '1.5',
      color: 'var(--txt-common)',
    },
    '& ol > li': {
      listStyle: 'decimal outside',
      position: 'relative',
      marginBottom: '0.5rem',
      lineHeight: '1.5',
      color: 'var(--txt-common)',
    },
    '& li:last-child': {
      marginBottom: '0',
    },
    '& li > p': {
      display: 'inline',
      margin: '0',
    },
  },
}));

const removeWatermark = (editors) => {
  const observer = new MutationObserver(() => {
    const poweredByElements = document.querySelectorAll(
      '.ck-powered-by, .ck-powered-by-balloon',
    );
    poweredByElements.forEach((element) => element.remove());
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  editors.on('destroy', () => {
    observer.disconnect();
  });
};

type Props = {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  onBlur?: (value: string) => void;
  extraLines?: number;
  showLists?: boolean;
  addTargetToExternalLinks?: boolean;
  FloatingActionButton?: React.ReactNode;
  style?: React.CSSProperties;
};

const CkEditor = ({
  value,
  onChange,
  placeholder,
  disabled,
  onBlur,
  extraLines = 0,
  showLists = true,
  addTargetToExternalLinks = false,
  FloatingActionButton = null,
  style = {},
}: Props) => {
  const isToolbarInteractionRef = useRef(false);
  const sanitiseHtml = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Remove meta tags
    tempDiv.querySelectorAll('meta').forEach((meta) => meta.remove());

    // Remove specific docs-internal-guid bold tag
    const guidBoldTag = tempDiv.querySelector('b[id^="docs-internal-guid"]');
    if (guidBoldTag) {
      tempDiv.innerHTML = guidBoldTag.innerHTML;
    }

    // Convert single br tags to paragraph breaks
    const brs = tempDiv.querySelectorAll('br');
    brs.forEach((br) => {
      if (
        br.parentElement?.tagName === 'DIV' ||
        br.parentElement?.tagName === 'BODY'
      ) {
        const p = document.createElement('p');
        p.innerHTML = '<br>';
        br.parentNode?.replaceChild(p, br);
      }
    });

    // Clean up styles
    tempDiv.querySelectorAll('[style]').forEach((el) => {
      el.removeAttribute('style');
    });

    // Apply inline styles to lists
    tempDiv.querySelectorAll('ol, ul').forEach((list) => {
      (list as HTMLElement).style.margin = '0.3rem 0 0.3rem 0';
      (list as HTMLElement).style.paddingLeft = '1.5rem';
      (list as HTMLElement).style.listStylePosition = 'outside';
    });

    tempDiv.querySelectorAll('ul > li').forEach((li) => {
      (li as HTMLElement).style.listStyle = 'disc outside';
    });

    tempDiv.querySelectorAll('ol > li').forEach((li) => {
      (li as HTMLElement).style.listStyle = 'decimal outside';
    });

    tempDiv.querySelectorAll('li').forEach((li) => {
      (li as HTMLElement).style.position = 'relative';
      (li as HTMLElement).style.marginBottom = '0.5rem';
      (li as HTMLElement).style.lineHeight = '1.5';
      (li as HTMLElement).style.color = 'var(--txt-common)';
    });

    tempDiv.querySelectorAll('li:last-child').forEach((li) => {
      (li as HTMLElement).style.marginBottom = '0';
    });

    tempDiv.querySelectorAll('li > p').forEach((p) => {
      (p as HTMLElement).style.display = 'inline';
      (p as HTMLElement).style.margin = '0';
    });

    return tempDiv.innerHTML;
  };

  const handlePaste = (event, data, editors) => {
    // Prevent the default paste behavior
    const textData = data.getData('text/html') || data.getData('text/plain');

    //if we detect a word document, we don't want to sanitize it and let CKEditor handle it
    if (isWordDocument(textData)) {
      return;
    }

    event.stop();

    try {
      const sanitisedHtml = sanitiseHtml(textData);

      // Get the editor model and create a batch for our changes
      const model = editors.model;
      const viewFragment = editors.data.htmlProcessor.toView(sanitisedHtml);
      const modelFragment = editors.data.toModel(viewFragment);

      // Insert the content at the current selection position
      model.insertContent(modelFragment);
    } catch (error) {
      console.error('Error processing paste:', error);
      // If there's an error, let the original paste proceed
    }
  };

  return (
    <EditorWrapper
      minHeight={extraLines > 0 ? 100 + extraLines * 20 : undefined}
      style={style}
    >
      <CKEditor
        className="document-editor-editor"
        editor={ClassicEditor}
        onReady={(editors) => {
          removeWatermark(editors);

          const editableElement = editors.ui.getEditableElement();
          if (editableElement) {
            editableElement.classList.add('text-component');
          }
          // Listen for the paste event
          editors.editing.view.document.on('clipboardInput', (event, data) => {
            const { dataTransfer } = data;
            handlePaste(event, dataTransfer, editors);
          });

          // Track toolbar interactions to prevent onBlur during toolbar clicks.
          const toolbar = editors.ui.view.toolbar.element;
          toolbar.addEventListener('mousedown', () => {
            isToolbarInteractionRef.current = true;
          });

          // Reset the flag after a short delay
          toolbar.addEventListener('mouseup', () => {
            setTimeout(() => {
              isToolbarInteractionRef.current = false;
            }, 100);
          });
        }}
        config={{
          toolbar: [
            '|',
            'bold',
            'italic',
            'link',
            'underline',
            ...(showLists ? ['bulletedList', 'numberedList'] : []),
          ],
          link: {
            addTargetToExternalLinks,
            ...(addTargetToExternalLinks
              ? { defaultProtocol: 'https://' }
              : {}),
          },
          list: {
            properties: {
              styles: false,
              startIndex: false,
              reversed: false,
            },
          },
          placeholder,
          pasteFromOffice: {
            keepZeroMargins: true,
            removeFontBackgroundColor: true,
            removeStyles: true,
            converterOptions: {
              preserveListIndentation: true,
            },
          },
          removePlugins: !showLists
            ? [
                'List',
                'ListUI',
                'ListStyle',
                'ListProperties',
                'TodoList',
                'ListEditing',
                'IndentList',
              ]
            : [],
        }}
        disabled={disabled}
        data={value || ''}
        onChange={(_, editor) => {
          const rawData = editor.getData();
          onChange?.(rawData);
        }}
        onBlur={(_, editor) => {
          // Don't process onBlur if it's from a toolbar interaction
          if (isToolbarInteractionRef.current) {
            return;
          }
          const rawData = editor.getData();
          const styledData = sanitiseHtml(rawData);
          onBlur?.(styledData);
        }}
      />
      {FloatingActionButton ? (
        <FloatingActionButtonContainer>
          {FloatingActionButton}
        </FloatingActionButtonContainer>
      ) : null}
    </EditorWrapper>
  );
};

const isWordDocument = (text) => {
  // Check for common Word document HTML markers
  const wordMarkers = [
    'urn:schemas-microsoft-com:office:word',
    'xmlns:w="urn:schemas-microsoft-com:office:word"',
    '<w:WordDocument>',
    'mso-',
    '<o:OfficeDocumentSettings>',
  ];

  return wordMarkers.some((marker) => text.includes(marker));
};

export default CkEditor;
